import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button, Input, Select } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { formatPhone } from '../lib/formatPhone';
import { useEntityPermission } from '../lib/usePermissions';
import { latestRowAttr } from '../lib/inlineAttribution';
import { LastUpdated } from './LastUpdated';
import { ContactCard, AddContactModal, EntityContact } from './EntityContactsPanel';

/**
 * ClientContactsGrouped — the client-profile Contacts tab (§5.1), grouped into
 * three rows: CLIENT-level, PROPERTY-level (with which property), and
 * PARCEL-level (with which parcel) contacts. Reads /api/client-contacts-grouped,
 * which reads each level directly (inheritance excluded), so a parcel contact
 * shows under "Parcel level" with its parcel — never duplicated up as a client
 * contact. Adding picks the level + records (§5.2/§5.4); editing reuses the
 * shared single-entity contact form scoped to that row's record.
 */

type Level = 'clients' | 'properties' | 'parcels';

interface GroupedContact extends EntityContact {
  record_id?: string;
  record_label?: string | null;
  property_label?: string | null;
}
interface Grouped { client: GroupedContact[]; property: GroupedContact[]; parcel: GroupedContact[]; }

const humanize = (t: string) => { const s = t.replace(/_/g, ' ').trim(); return s.charAt(0).toUpperCase() + s.slice(1); };
function normalizeValues(data: string[] | { values: string[] } | undefined): string[] {
  if (!data) return [];
  return Array.isArray(data) ? data : (data.values || []);
}

export function ClientContactsGrouped({ clientId }: { clientId: string }) {
  const canAdd = useEntityPermission('contact', 'can_add');
  const canEdit = useEntityPermission('contact', 'can_edit');
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState<{ contact: EntityContact; entityType: Level; entityId: string } | null>(null);

  const key = useMemo(() => ['client-contacts-grouped', clientId], [clientId]);
  const q = useQuery<Grouped>({
    queryKey: key,
    queryFn: ({ signal }) => api.get<Grouped>(`/api/client-contacts-grouped/${clientId}`, signal),
    enabled: !!clientId,
  });
  const data = q.data ?? { client: [], property: [], parcel: [] };
  const total = data.client.length + data.property.length + data.parcel.length;
  // A5 — one section attribution for the whole Contacts tab: the most-recent
  // contact edit across all three levels.
  const attr = latestRowAttr([...data.client, ...data.property, ...data.parcel]);

  const sections: { key: keyof Grouped; label: string; rows: GroupedContact[]; entityType: Level; blurb: string }[] = [
    { key: 'client', label: 'Client level', rows: data.client, entityType: 'clients', blurb: 'For the client overall — not tied to a specific property or parcel.' },
    { key: 'property', label: 'Property level', rows: data.property, entityType: 'properties', blurb: 'Tied to specific properties.' },
    { key: 'parcel', label: 'Parcel level', rows: data.parcel, entityType: 'parcels', blurb: 'Tied to specific parcels.' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
            {q.isLoading ? 'Loading contacts…' : `${total} contact${total === 1 ? '' : 's'} across client, property, and parcel levels`}
          </span>
          {attr && <LastUpdated updatedBy={attr.by} updatedAt={attr.at} />}
        </div>
        {canAdd && <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>+ Add contact</Button>}
      </div>

      {q.error && (
        <div style={{ padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-lg)' }}>
          {(q.error as Error).message}
        </div>
      )}

      {!q.isLoading && !q.error && total === 0 && (
        <div style={{ padding: 'var(--sp-6)', background: 'var(--color-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-md)' }}>
          No contacts on this client yet.{canAdd ? ' Use “+ Add contact” to link one at the client, property, or parcel level.' : ''}
        </div>
      )}

      {!q.isLoading && total > 0 && sections.map((s) => (
        <section key={s.key} style={{ marginBottom: 'var(--sp-5)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{s.label}</h3>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>({s.rows.length}) · {s.blurb}</span>
          </div>
          {s.rows.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>None at this level.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 'var(--sp-4)' }}>
              {s.rows.map((c) => (
                <ContactCard
                  key={`${c.id}-${c.record_id ?? ''}-${c.role ?? ''}`}
                  contact={c}
                  canEdit={canEdit}
                  onEdit={() => setEdit({ contact: c, entityType: s.entityType, entityId: s.entityType === 'clients' ? clientId : (c.record_id || clientId) })}
                  coverage={
                    s.entityType === 'properties' ? <>Property: <strong>{c.record_label || '—'}</strong></>
                      : s.entityType === 'parcels' ? <>Parcel: <strong>{c.record_label || '—'}</strong>{c.property_label ? <> · {c.property_label}</> : null}</>
                      : null
                  }
                />
              ))}
            </div>
          )}
        </section>
      ))}

      <AddContactMultiModal open={addOpen} onClose={() => setAddOpen(false)} clientId={clientId} invalidateKey={key} />

      {edit && (
        <AddContactModal
          open
          editContact={edit.contact}
          entityType={edit.entityType}
          entityId={edit.entityId}
          invalidateKey={key}
          onClose={() => setEdit(null)}
        />
      )}
    </div>
  );
}

interface NamedRow { id: string; name: string | null; account_number?: string | null }

function AddContactMultiModal({
  open, onClose, clientId, invalidateKey,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  invalidateKey: unknown[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [level, setLevel] = useState<Level>('clients');
  const [propIds, setPropIds] = useState<Set<string>>(new Set());
  const [parcelProp, setParcelProp] = useState('');
  const [parcelIds, setParcelIds] = useState<Set<string>>(new Set());
  const [form, setForm] = useState({ first_name: '', last_name: '', company_name: '', title: '', type: '', role: '', email: '', phone: '' });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const propsQuery = useQuery<NamedRow[]>({
    queryKey: ['properties-by-client', clientId],
    queryFn: ({ signal }) => api.get<NamedRow[]>(`/api/properties?client_id=${clientId}`, signal),
    enabled: open,
  });
  const parcelsQuery = useQuery<NamedRow[]>({
    queryKey: ['parcels-by-property', parcelProp],
    queryFn: ({ signal }) => api.get<NamedRow[]>(`/api/parcels?property_id=${parcelProp}`, signal),
    enabled: open && level === 'parcels' && !!parcelProp,
  });
  const typesQuery = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'contact-types'], queryFn: ({ signal }) => api.get('/api/org-settings/contact-types', signal), enabled: open, staleTime: 5 * 60 * 1000 });
  const rolesQuery = useQuery<string[] | { values: string[] }>({ queryKey: ['org-settings', 'contact-roles'], queryFn: ({ signal }) => api.get('/api/org-settings/contact-roles', signal), enabled: open, staleTime: 5 * 60 * 1000 });

  const properties = propsQuery.data ?? [];
  const parcels = parcelsQuery.data ?? [];
  const typeOptions = [{ value: '', label: '— Select type —' }, ...normalizeValues(typesQuery.data).map((t) => ({ value: t, label: humanize(t) }))];
  const roleOptions = [{ value: '', label: 'Primary (default)' }, ...normalizeValues(rolesQuery.data).map((r) => ({ value: r, label: humanize(r) }))];

  const canSubmit = (form.first_name.trim() || form.last_name.trim() || form.company_name.trim())
    && (level === 'clients' || (level === 'properties' && propIds.size > 0) || (level === 'parcels' && parcelIds.size > 0));

  const reset = () => { setLevel('clients'); setPropIds(new Set()); setParcelProp(''); setParcelIds(new Set()); setForm({ first_name: '', last_name: '', company_name: '', title: '', type: '', role: '', email: '', phone: '' }); setError(null); };

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => {
      const targets = level === 'clients'
        ? [{ entity_type: 'clients', entity_id: clientId }]
        : level === 'properties'
          ? [...propIds].map((id) => ({ entity_type: 'properties', entity_id: id }))
          : [...parcelIds].map((id) => ({ entity_type: 'parcels', entity_id: id }));
      const contact: Record<string, string> = {};
      for (const [k, v] of Object.entries(form)) if (v.trim() !== '') contact[k] = v.trim();
      return api.post('/api/entity-contacts-bulk', { contact, targets });
    },
    onSuccess: () => {
      toast({ variant: 'success', message: 'Contact added.' });
      qc.invalidateQueries({ queryKey: invalidateKey });
      // Also refresh the BFF the tab badge + header "Contacts" KPI read from.
      qc.invalidateQueries({ queryKey: ['client-view', clientId] });
      reset();
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body ? String((err.body as { error: string }).error) : err.message;
      setError(msg);
      toast({ variant: 'error', message: msg });
    },
  });

  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => setter((prev) => {
    const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const allParcelsSelected = parcels.length > 0 && parcels.every((p) => parcelIds.has(p.id));

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) { reset(); onClose(); } }}
      title="Add contact"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => { reset(); onClose(); }} disabled={mutation.isPending}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!canSubmit} onClick={() => mutation.mutate()}>Add contact</Button>
        </>
      }
    >
      {error && (
        <div style={{ padding: 'var(--sp-3) var(--sp-4)', marginBottom: 'var(--sp-4)', background: 'var(--color-status-red-bg)', border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)' }}>{error}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <Field label="Level" hint="Property and parcel are optional — a client-level contact needs neither.">
          <Select
            options={[{ value: 'clients', label: 'Client level' }, { value: 'properties', label: 'Property level' }, { value: 'parcels', label: 'Parcel level' }]}
            value={level}
            onChange={(e) => { setLevel(e.target.value as Level); setPropIds(new Set()); setParcelIds(new Set()); setParcelProp(''); }}
          />
        </Field>

        {level === 'properties' && (
          <Field label="Properties" hint="Select one or more — a contact can cover several properties.">
            <CheckList items={properties.map((p) => ({ id: p.id, label: p.name || p.id }))} selected={propIds} onToggle={(id) => toggle(setPropIds, id)} empty="This client has no properties yet." loading={propsQuery.isLoading} />
          </Field>
        )}

        {level === 'parcels' && (
          <>
            <Field label="Property" hint="Pick the property whose parcels this contact covers.">
              <Select
                options={[{ value: '', label: '— Select property —' }, ...properties.map((p) => ({ value: p.id, label: p.name || p.id }))]}
                value={parcelProp}
                onChange={(e) => { setParcelProp(e.target.value); setParcelIds(new Set()); }}
              />
            </Field>
            {parcelProp && (
              <Field label="Parcels" hint="Different contacts can cover different subsets of a property's parcels.">
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-2)', fontSize: 'var(--fs-sm)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={allParcelsSelected}
                      onChange={(e) => setParcelIds(e.target.checked ? new Set(parcels.map((p) => p.id)) : new Set())}
                    />
                    Select all parcels
                  </label>
                  <CheckList items={parcels.map((p) => ({ id: p.id, label: p.account_number || p.name || p.id }))} selected={parcelIds} onToggle={(id) => toggle(setParcelIds, id)} empty="This property has no parcels yet." loading={parcelsQuery.isLoading} />
                </>
              </Field>
            )}
          </>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 'var(--sp-3)' }}>
          <Field label="First name"><Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} /></Field>
          <Field label="Last name"><Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} /></Field>
          <Field label="Company"><Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} /></Field>
          <Field label="Title"><Input value={form.title} onChange={(e) => set('title', e.target.value)} /></Field>
          <Field label="Type"><Select options={typeOptions} value={form.type} onChange={(e) => set('type', e.target.value)} /></Field>
          <Field label="Role"><Select options={roleOptions} value={form.role} onChange={(e) => set('role', e.target.value)} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set('phone', formatPhone(e.target.value))} inputMode="tel" placeholder="XXX-XXX-XXXX" /></Field>
        </div>
      </div>
    </Modal>
  );
}

function CheckList({ items, selected, onToggle, empty, loading }: { items: { id: string; label: string }[]; selected: Set<string>; onToggle: (id: string) => void; empty: string; loading: boolean }) {
  if (loading) return <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>;
  if (items.length === 0) return <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>{empty}</p>;
  return (
    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' }}>
      {items.map((it) => (
        <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', padding: 'var(--sp-2) var(--sp-3)', cursor: 'pointer', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--fs-sm)' }}>
          <input type="checkbox" checked={selected.has(it.id)} onChange={() => onToggle(it.id)} />
          {it.label}
        </label>
      ))}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-2)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{hint}</span>}
    </label>
  );
}
